import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { ToolNewPostionSilce, NewlyCreatedFacet, OBS_SHAPE, EditedObstruction, OBS_EDIT_POINT_ACTION } from './types';
import { changePanel, fetchRoofData } from '../RoofDataSlice/roofDataSlice';
import { initializeState, updateFacetHistory } from './util';
import { FACET_TYPE } from '../../../constants';

const initialState: { data: ToolNewPostionSilce } = {
	data: initializeState()
};

function resetUnfinalizedState(state: ToolNewPostionSilce) {
	state.newlyCreatedFacets = {};
	state.selectedRoofIndex = null;
	state.selectedRoofAzimuth = null;
	state.selectedUnfinalizedKonvaFacetId = null;
	state.deletedRoofIndexes = [];
	state.userHasModifiedRoofSegs = false;
	state.roofIndexes = [];
	state.hadTransition = false;
	state.deleteObstructionsPopUp = false;
	state.deleteObstructionsMode = false;
	state.enabledDevMode = false;
	state.invalidAzimuthFacets = {};
	state.azimuthEditedFacets = {};
	state.anyInvalidAzimuth = false;
	state.confirmedExceedingAzimuthFacets = [];
	state.addObstructionsMode = false;
	state.addObstructionsPopUp = false;
	state.obstructionShape = 'CIRCLE' as ToolNewPostionSilce['obstructionShape'];
	state.showSetback = false;
	state.fullModePopupOpen = false;
	state.deleteFacetMode = false;
	state.multiFingersPress = false;
	state.isCancelModalOpen = false;
	state.drawnFacetExists = false;
	state.enableEditObs = false;
	state.editedObstructions = {};
	state.drawnObsExists = false;
	state.selectedObstructionId = null;
	state.obstructionEditPointAction = null;
	state.pendingAzimuthConfirmationFacet = null;
	state.isTransformingAzimuth = false;
	state.guideMode = false;
	state.snapMode = false;
	state.hideSatelliteOverlay = false;
}

// The side panel (which holds ModeSelector) can be freely minimized/maximized by the user
// while 3D is open, but must snap back to whatever it was set to right before 3D opened.
function restoreSidePanelStateBeforeThreeD(state: ToolNewPostionSilce) {
	if (state.fullViewSidePanelBeforeThreeD !== null) {
		state.fullViewSidePanel = state.fullViewSidePanelBeforeThreeD;
		state.fullViewSidePanelBeforeThreeD = null;
	}
}

export const ToolNewPostionSlice = createSlice({
	name: 'ToolPositionsSlice',
	initialState,
	reducers: {
		setRoofIndexes: (state, action: PayloadAction<{ roofIndexes: string[] }>) => {
			state.data.roofIndexes = action.payload.roofIndexes;
		},
		setSelectedRoofIndex: (state, action: PayloadAction<{ selectedRoofIndex: string|null }>) => {
			state.data.selectedRoofIndex = action.payload.selectedRoofIndex;
		},
		setSelectedRoofAzimuth: (state, action: PayloadAction<{ selectedRoofAzimuth: number|null }>) => {
			state.data.selectedRoofAzimuth = action.payload.selectedRoofAzimuth;
		},
		setEditShadingPerfomed: (state, action: PayloadAction<{ editShadingPerformed: boolean }>) => {
			state.data.editShadingPerformed = action.payload.editShadingPerformed;
		},
		setDeletedRoofIndexes: (state, action: PayloadAction<{ deletedRoofIndexes: string[] }>) => {
			const currentDeletedRoofIndexes = action.payload.deletedRoofIndexes;
			state.data.deletedRoofIndexes = currentDeletedRoofIndexes;
			if (currentDeletedRoofIndexes?.length) {
				const newState = { deletedRoofIndexes: currentDeletedRoofIndexes };
				updateFacetHistory(state.data.facetHistory.deleteFacet, newState);
			} else {
				state.data.facetHistory = initialState.data.facetHistory;
			}
			
		},
		/** direct assignment used by facet merging, which keeps its own undo/redo history */
		syncDeletedRoofIndexes: (state, action: PayloadAction<{ deletedRoofIndexes: string[] }>) => {
			state.data.deletedRoofIndexes = action.payload.deletedRoofIndexes;
		},
		setNewlyCreatedFacets: (state, action: PayloadAction<{ newlyCreatedFacet: NewlyCreatedFacet; }>) => {
			state.data.newlyCreatedFacets = {
				...state.data.newlyCreatedFacets,
				[action.payload.newlyCreatedFacet.konvaPolygonId]: action.payload.newlyCreatedFacet
			};
		},
		setAzimuthOrPitchForNewlyCreatedNrelFacets:
			(state, action: PayloadAction<{
				facetId: string, azimuthDegrees?: number, pitchDegrees?: number,
				shading?: number, rowSpacing?: number, startingHeight?: number, isEdited?: boolean
			}>) => {
				const { facetId, azimuthDegrees, pitchDegrees, shading, rowSpacing, startingHeight, isEdited } = action.payload;
				if ((!azimuthDegrees && azimuthDegrees != 0) && (!pitchDegrees && pitchDegrees != 0) &&
					(!shading && shading != 0) && (!rowSpacing && rowSpacing != 0) &&
					(!startingHeight && startingHeight != 0)) {
					throw new Error('azimuthDegrees or pitchDegrees or shading or rowSpacing or startingHeight is required');
				}

				if(!state.data.newlyCreatedFacets[facetId]) return;

				const facetObj = state.data.newlyCreatedFacets[facetId];
				state.data.newlyCreatedFacets = {
					...state.data.newlyCreatedFacets,
					[facetId]: {
						...facetObj,
						azimuthDegrees: azimuthDegrees ?? facetObj.azimuthDegrees,
						pitchDegrees: pitchDegrees ?? facetObj.pitchDegrees,
						shading: shading ?? facetObj.shading,
						rowSpacing: rowSpacing ?? facetObj.rowSpacing,
						startingHeight: startingHeight ?? facetObj.startingHeight,
						isEdited: isEdited ?? false,
					}
				};

			},
		removeNewlyCreatedFacets: (state, action: PayloadAction<{ id: string }>) => {
			const { id } = action.payload;

			const filteredFacets = Object.fromEntries(
				Object.entries(state.data.newlyCreatedFacets).filter(
					([key]) => key !== id
				)
			);

			state.data.newlyCreatedFacets = filteredFacets;
		},
		resetNewlyCreatedFacets: (state) => {
			state.data.newlyCreatedFacets = {};
			state.data.deletedRoofIndexes = [];
		},
		setEditState: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.editModeEnabled = action.payload.enabled;
			state.data.drawModeEnabled = false;
			state.data.toggleGroundMountEnabled = false;
			resetUnfinalizedState(state.data);
		},
		setThreeDState: (state, action: PayloadAction<{ enabled: boolean }>) => {
			if (action.payload.enabled && !state.data.threeDModelEnabled) {
				state.data.fullViewSidePanelBeforeThreeD = state.data.fullViewSidePanel;
			} else if (!action.payload.enabled && state.data.threeDModelEnabled) {
				restoreSidePanelStateBeforeThreeD(state.data);
			}
			state.data.threeDModelEnabled = action.payload.enabled;
			state.data.drawModeEnabled = false;
			state.data.toggleGroundMountEnabled = false;
			state.data.threeDViewLoaded = false;
			// Position/rotation reset to center/north on exiting 3D, same as every other reset
			// trigger (resize, fullscreen, imagery/mode switch, save) — see useKonvaStagePos.ts's
			// 3D-exit restore effect, which resets stage rotation to 0 and recenters.
			resetUnfinalizedState(state.data);
		},
		setSaveWithPanelColorState: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.saveWithPanelColor = action.payload.enabled;
		},
		setUserHasClickedSave: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.userHasClickedSave = action.payload.enabled;
		},
		setDrawModeState: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.drawModeEnabled = action.payload.enabled;
			state.data.toggleGroundMountEnabled = true;
			state.data.selectedUnfinalizedKonvaFacetId = null;
			state.data.selectedRoofIndex = null;
			state.data.newlyCreatedFacets = {};
			if (!action.payload.enabled) {
				state.data.guideMode = false;
				state.data.snapMode = false;
			}
		},
		setUnfinalizedFacet: (state, action: PayloadAction<{ id: string | null }>) => {
			const { id: facetID, } = action.payload;
			state.data.selectedUnfinalizedKonvaFacetId = facetID;
		},
		setZoomLevel: (state, action: PayloadAction<{ scaleBy: number, position?: Vector2d }>) => {
			const { scaleBy, position = initialState.data.konvaStagePosition } = action.payload;
			state.data.zoomLevel = scaleBy;
			state.data.konvaStagePosition = position;
		},
		setMoveStage: (state, action: PayloadAction<{ shouldMoveStage: boolean }>) => {
			state.data.moveStage = action.payload.shouldMoveStage;
		},
		setRotateStage: (state, action: PayloadAction<{ shouldRotateStage: boolean }>) => {
			state.data.rotateStage = action.payload.shouldRotateStage;
		},
		setStageRotation: (state, action: PayloadAction<{ rotation: number }>) => {
			state.data.stageRotation = action.payload.rotation;
		},
		setKonvaStagePosition: (state, action: PayloadAction<{ position: Vector2d }>) => {
			state.data.konvaStagePosition = action.payload.position;
		},
		setMouseState: (state, action: PayloadAction<{ mouseOnMap: boolean }>) => {
			state.data.isMouseOnMap = action.payload.mouseOnMap;
		},
		setUserModificationState: (state, action: PayloadAction<{ hasMadeChanges: boolean }>) => {
			state.data.userHasModifiedRoofSegs = action.payload.hasMadeChanges;
		},
		setFullScreenState: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.fullScreenEnabled = action.payload.enabled;
			// if(!action.payload.enabled){
			// 	state.data.fullViewSidePanel = false;
			// }
		},
		setProvider: (state, action: PayloadAction<{ currentProvider: ImagerySource }>) => {
			state.data.selectedProvider = action.payload.currentProvider;
			if (state.data.threeDModelEnabled) restoreSidePanelStateBeforeThreeD(state.data);
			state.data.threeDModelEnabled = false;
			state.data.threeDViewLoaded = false;
		},
		toggleGroundMount: (state, action: PayloadAction<{ groundMountEnabled: boolean}>) =>{
			state.data.groundMountEnabled = action.payload.groundMountEnabled;
			state.data.toggleNoGapGroundMountEnabled = action.payload.groundMountEnabled; // as this option will only be available in ground mount
			state.data.noGapGroundMountEnabled = false;
		},
		toggleNoGapGroundMount: (state, action: PayloadAction<{ noGapGroundMountEnabled: boolean }>) =>{
			state.data.noGapGroundMountEnabled = action.payload.noGapGroundMountEnabled;
		},
		toggleGuideMode: (state) => {
			state.data.guideMode = !state.data.guideMode;
			// snap depends on guide, so turning guide off also turns snap off
			if (!state.data.guideMode) state.data.snapMode = false;
		},
		setGuideMode: (state, action: PayloadAction<boolean>) => {
			state.data.guideMode = action.payload;
			if (!action.payload) state.data.snapMode = false;
		},
		toggleSnapMode: (state) => {
			state.data.snapMode = !state.data.snapMode;
			// snap depends on guide, so turning snap on also turns guide on
			if (state.data.snapMode) state.data.guideMode = true;
		},
		setSnapMode: (state, action: PayloadAction<boolean>) => {
			state.data.snapMode = action.payload;
			if (action.payload) state.data.guideMode = true;
		},
		toggleHideSatelliteOverlay: (state) => {
			state.data.hideSatelliteOverlay = !state.data.hideSatelliteOverlay;
		},
		setActiveDragFunctionality: (state, action: PayloadAction<string | null>) => {
			state.data.activeDragFunctionality = action.payload;
		},
		setMapOnlyFrozenPoint: (state, action: PayloadAction<{ x: number; y: number } | null>) => {
			state.data.mapOnlyFrozenPoint = action.payload;
		},
		updateTransition: (state, action: PayloadAction<{currentTransitionState: boolean}>) => {
			state.data.hadTransition = action.payload.currentTransitionState;
		},
		setDeletedObstructionIDs: (state, action: PayloadAction<{obstructionID: string}>)=> {
			const currentDeletedObstructionIDs = [...state.data.deletedObstructionIDs, action.payload.obstructionID];
			const newState = { deletedObstructionIDs: currentDeletedObstructionIDs };
			state.data.deletedObstructionIDs = currentDeletedObstructionIDs;
			state.data.invalidObstructionIds = state.data.invalidObstructionIds.filter((id) => id !== action.payload.obstructionID);
			updateFacetHistory(state.data.facetHistory.obstruction, newState);
		},
		setDeletedObstructionIDsBulk: (state, action: PayloadAction<{ obstructionIDs: string[] }>) => {
			state.data.deletedObstructionIDs = action.payload.obstructionIDs;
			const deletedSet = new Set(action.payload.obstructionIDs);
			state.data.invalidObstructionIds = state.data.invalidObstructionIds.filter((id) => !deletedSet.has(id));
		},
		toggleDeleteObstructionsMode: (state, action: PayloadAction<{enable: boolean}>)=>{
			state.data.deleteObstructionsMode = action.payload.enable;
		},
		resetDeletedObstructionIDs: (state)=> {
			state.data.deletedObstructionIDs = [];
			state.data.facetHistory= initialState.data.facetHistory;
		},
		undoEditFacetState: (state) => {
			if (state.data.deleteObstructionsMode) {
				const hist = state.data.facetHistory.obstruction;
				if (hist.currentIndex < 0) {
					return;
				}
				if(hist.currentIndex === 0){	
					hist.currentIndex = -1;
					state.data.facetHistory.obstruction.currentIndex = -1;
					state.data.deletedObstructionIDs = [];
					return;
				}

				hist.currentIndex -= 1;
				const prev = hist.history[hist.currentIndex];
				state.data.deletedObstructionIDs = prev.deletedObstructionIDs;
			}

			if (state.data.deleteFacetMode) {
				const hist = state.data.facetHistory.deleteFacet;
				if (hist.currentIndex < 0) {
					return;
				}
				if(hist.currentIndex === 0){	
					hist.currentIndex = -1;
					state.data.facetHistory.deleteFacet.currentIndex = -1;
					state.data.deletedRoofIndexes = [];
					return;
				}

				hist.currentIndex -= 1;
				const prev = hist.history[hist.currentIndex];
				state.data.deletedRoofIndexes = prev.deletedRoofIndexes;
			}
		},
		redoEditFacetState: (state) => {
			if (state.data.deleteObstructionsMode) {
				const hist = state.data.facetHistory.obstruction;
				if (hist.currentIndex >= hist.history.length - 1) return;

				hist.currentIndex += 1;
				const next = hist.history[hist.currentIndex];
				state.data.deletedObstructionIDs = next.deletedObstructionIDs;
			}

			if (state.data.deleteFacetMode) {
				const hist = state.data.facetHistory.deleteFacet;
				if (hist.currentIndex >= hist.history.length - 1) return;

				hist.currentIndex += 1;
				const next = hist.history[hist.currentIndex];
				state.data.deletedRoofIndexes = next.deletedRoofIndexes;
			}
		},
		showDeleteObstructionsPopUp: (state, action: PayloadAction<{enable: boolean}>) => {
			state.data.deleteObstructionsPopUp = action.payload.enable;
		},
		setDevMode:(state, action:PayloadAction<{enable: boolean}>) => {
			state.data.enabledDevMode = action.payload.enable;
		},
		setInvalidAzimuthFacet:(state, action:PayloadAction<{facetName: string, invalid: boolean}>) => {
			const { facetName, invalid } = action.payload;
			state.data.invalidAzimuthFacets = {
				...state.data.invalidAzimuthFacets,
				[facetName]: invalid,
			};
			state.data.anyInvalidAzimuth = Object.values(state.data.invalidAzimuthFacets).some(invalid => invalid);
		},
		setEditedAzimuthFacet:(state, action:PayloadAction<{facetName: string, edited: boolean}>) => {
			const { facetName, edited } = action.payload;
			state.data.azimuthEditedFacets = {
				...state.data.azimuthEditedFacets,
				[facetName]: edited,
			};
		},
		setConfirmedExceedAzimuthWarningFacet:(state, action:PayloadAction<{facetName: string}>) => {
			const { facetName } = action.payload;
			state.data.confirmedExceedingAzimuthFacets = [
				...state.data.confirmedExceedingAzimuthFacets,
				facetName
			];
			state.data.anyInvalidAzimuth = Object.values(state.data.invalidAzimuthFacets).some(invalid => invalid);
		},
		resetAzimuthEditState: (state) => {
			state.data.azimuthEditedFacets = {};
			state.data.invalidAzimuthFacets = {};
			state.data.confirmedExceedingAzimuthFacets = [];
			state.data.anyInvalidAzimuth = false;
			state.data.pendingAzimuthConfirmationFacet = null;
			state.data.isTransformingAzimuth = false;
		},
		toggleShowSetback: (state, action: PayloadAction<{ showSetback: boolean }>) => {
			state.data.showSetback = action.payload.showSetback;
		},
		toggleAddObstructionsMode: (state, action: PayloadAction<{enable: boolean, shape?: OBS_SHAPE}>)=>{
			state.data.addObstructionsMode = action.payload.enable;
			state.data.obstructionShape = action.payload.shape ?? OBS_SHAPE.CIRCLE;
		},
		showAddObstructionsPopUp: (state, action: PayloadAction<{enable: boolean}>) => {
			state.data.addObstructionsPopUp = action.payload.enable;
		},
		setObstructionShape: (state, action: PayloadAction<{shape: OBS_SHAPE}>) => {
			state.data.obstructionShape = action.payload.shape;
		},
		setManualObs: (state, action: PayloadAction<{ newlyAddedObs: Obstruction; }>) => {
			state.data.manualObs = {
				...state.data.manualObs,
				[action.payload.newlyAddedObs.id]: action.payload.newlyAddedObs
			};
		},
		resetManualObs: (state) => {
			state.data.manualObs = {};
			state.data.obstructionShape = OBS_SHAPE.CIRCLE;
			const editedIds = new Set(Object.keys(state.data.editedObstructions));
			state.data.invalidObstructionIds = state.data.invalidObstructionIds.filter((id) => editedIds.has(id));
		},
		removeNewlyCreatedObs: (state, action: PayloadAction<{ id: string }>) => {
			const { id } = action.payload;

			const filteredManualObs = Object.fromEntries(
				Object.entries(state.data.manualObs).filter(
					([key]) => key !== id
				)
			);

			state.data.manualObs = filteredManualObs;
			state.data.invalidObstructionIds = state.data.invalidObstructionIds.filter((obsId) => obsId !== id);
		},
		toggleFullViewModeModal: (state, action: PayloadAction<{ open: boolean, warningMessge?: string }>) => {
			state.data.fullModePopupOpen = action.payload.open;
			state.data.fullModePopupMessgage = action.payload.warningMessge || '';
		},
		toggleLinearFtForSolarVisibilty: (state, action: PayloadAction<{ visible: boolean}>) => {
			state.data.showLinearFtForSolar = action.payload.visible;
		},
		toggleDeleteFacetsMode: (state, action: PayloadAction<{enable: boolean}>)=>{
			state.data.deleteFacetMode = action.payload.enable;
			
			if (!action.payload.enable) {
				state.data.facetHistory = initialState.data.facetHistory;
				state.data.deletedRoofIndexes = [];
			}

		},
		setMultiFingersPress: (state, action: PayloadAction<{enabled: boolean}>) => {
			state.data.multiFingersPress = action.payload.enabled;
		},

		toggleFullViewSidePanel: (state, action: PayloadAction<{enabled: boolean}>) => {
			state.data.fullViewSidePanel = action.payload.enabled;
		},
		setThreeDViewLoaded: (state, action: PayloadAction<{loaded: boolean}>) => {
			state.data.threeDViewLoaded = action.payload.loaded;
			state.data.fullViewSidePanel = false;
		},
		setCancelModalOpen: (state, action: PayloadAction<{ isOpen: boolean }>) => {
			state.data.isCancelModalOpen = action.payload.isOpen;
		},
		setDrawnFacetExists: (state, action: PayloadAction<{ exists: boolean }>) => {
			state.data.drawnFacetExists = action.payload.exists;
		},
		setSelectedObstructionId: (state, action: PayloadAction<{ id: string | null }>) => {
			state.data.selectedObstructionId = action.payload.id;
			if (action.payload.id === null) {
				state.data.obstructionEditPointAction = null;
			}
			if (action.payload.id === null && state.data.enableEditObs && !state.data.deleteObstructionsMode) {
				state.data.addObstructionsMode = true;
			}
		},

		setEnableObsMode: (state, action: PayloadAction<{ enable: boolean }>) => {
			state.data.enableEditObs = action.payload.enable;
			state.data.addObstructionsMode = action.payload.enable; 
			state.data.selectedObstructionId = null; 
			if (!action.payload.enable) {
				state.data.obstructionEditPointAction = null;
				state.data.invalidObstructionIds = [];
			}
		},
		setObstructionEditPointAction: (
			state,
			action: PayloadAction<{ actionType: OBS_EDIT_POINT_ACTION | null }>
		) => {
			state.data.obstructionEditPointAction = action.payload.actionType;
		},
		setEditedObstruction: (state, action: PayloadAction<EditedObstruction>) => {
			state.data.editedObstructions[action.payload.id] = action.payload;
		},
		resetEditedObstructions: (state) => {
			state.data.editedObstructions = {};
			state.data.drawnObsExists = false;
			const manualIds = new Set(Object.keys(state.data.manualObs));
			state.data.invalidObstructionIds = state.data.invalidObstructionIds.filter((id) => manualIds.has(id));
		},
		setInvalidObstructionState: (state, action: PayloadAction<{ id: string; invalid: boolean }>) => {
			const { id, invalid } = action.payload;
			const next = new Set(state.data.invalidObstructionIds);
			if (invalid) {
				next.add(id);
			} else {
				next.delete(id);
			}
			state.data.invalidObstructionIds = Array.from(next);
		},
		setDrawnObsExists: (state, action: PayloadAction<{ exists: boolean }>) => {
			state.data.drawnObsExists = action.payload.exists;
		},
		setIsTransforming: (state, action: PayloadAction<{ isTransformingAzimuth: boolean }>) => {
			state.data.isTransformingAzimuth = action.payload.isTransformingAzimuth;
		},
		setPendingAzimuthConfirmationFacet: (state, action: PayloadAction<{ facetName: string | null }>) => {
			state.data.pendingAzimuthConfirmationFacet = action.payload.facetName;
		},
	}, extraReducers: (builder,) => {
		builder.addCase(changePanel.fulfilled, (state, action) => {
			// Skip these when the refetch isn't the user's own edit (retainDrawingSessionOnUi) —
			// otherwise it wipes an in-progress facet-draw or obstruction-edit draft that's
			// unrelated to this refetch (e.g. a background shading-recalc refresh).
			const retainDrawingSessionOnUi = action.meta.arg.retainDrawingSessionOnUi;
			// switchingSource marks an imagery or mode change (ModeSelector/SwitchProvider both set
			// it) as opposed to a plain edit refetch (facet draw, obstruction, etc.) sharing this same
			// action — only the former resets the view, same as every other reset trigger.
			const isImageryOrModeSwitch = !!action.meta.arg.switchingSource;
			state.data = {
				...state.data,
				...(!retainDrawingSessionOnUi && {
					newlyCreatedFacets: {},
					drawModeEnabled: false,
					groundMountEnabled: (
						action.payload.roofData.missingRoofPolygon ||
						(action.payload.roofData.designType === 'SOLAR' &&
							action.payload.roofData.org.setting.defaultDrawnFacetType === FACET_TYPE.FLAT)
					),
					deletedObstructionIDs: [],
					deleteObstructionsMode: false,
					addObstructionsMode: false,
					obstructionShape: OBS_SHAPE.CIRCLE,
					guideMode: false,
					snapMode: false,
				}),
				// deletedRoofIndexes:[],
				selectedRoofIndex: null,
				selectedUnfinalizedKonvaFacetId: null,
				userHasModifiedRoofSegs: false,
				facetHistory: initialState.data.facetHistory,
				toggleGroundMountEnabled: false,
				// A plain edit refetch (facet draw, obstruction, etc.) must not snap the user's
				// current view back — rotation is left untouched; position already persists via the
				// same path. An imagery or mode switch resets rotation to north, same as every other
				// reset trigger (position/zoom there are reset separately, by useKonvaStagePos.ts's
				// scale effect reacting to the imgHeight change this brings).
				...(isImageryOrModeSwitch && { stageRotation: initialState.data.stageRotation }),
			};
		}).addCase(fetchRoofData.fulfilled, (state, action) => {
			state.data = {
				...state.data,
				zoomLevel: action.payload.toolEditConfig.zoomLevel,
				selectedProvider: action.payload.roofData.imagerySource || initialState.data.selectedProvider,
				groundMountEnabled: (
					action.payload.roofData.missingRoofPolygon || 
					(action.payload.roofData.designType === 'SOLAR' && action.payload.roofData.org.setting.defaultDrawnFacetType === FACET_TYPE.FLAT)
				),
			};
		}).addCase(changePanel.rejected, (state, action) => {
			if(action.meta.arg.switchingSource){
				state.data.selectedProvider= initialState.data.selectedProvider;
			}
		});
	}
});

export const {
	setRoofIndexes, setEditState, setThreeDState, setSaveWithPanelColorState, setUserHasClickedSave,
	setSelectedRoofIndex, setDeletedRoofIndexes, syncDeletedRoofIndexes, setAzimuthOrPitchForNewlyCreatedNrelFacets, removeNewlyCreatedFacets,
	setNewlyCreatedFacets, resetNewlyCreatedFacets, setUnfinalizedFacet, setDrawModeState, setZoomLevel, setMoveStage, setMouseState,
	setRotateStage, setStageRotation,
	setKonvaStagePosition, setUserModificationState, setProvider, setFullScreenState,
	updateTransition, setDeletedObstructionIDs, 
	toggleDeleteObstructionsMode, resetDeletedObstructionIDs, undoEditFacetState, redoEditFacetState,
	showDeleteObstructionsPopUp, toggleGroundMount, setEditShadingPerfomed, toggleNoGapGroundMount,
	setSelectedRoofAzimuth, setDevMode, setEditedAzimuthFacet, setInvalidAzimuthFacet, resetAzimuthEditState, toggleAddObstructionsMode,
	showAddObstructionsPopUp, setObstructionShape, setManualObs, resetManualObs, removeNewlyCreatedObs, toggleShowSetback, toggleFullViewModeModal, 
	toggleLinearFtForSolarVisibilty,toggleDeleteFacetsMode, setMultiFingersPress, toggleFullViewSidePanel, setThreeDViewLoaded, setCancelModalOpen,
	setDrawnFacetExists, setEnableObsMode, setDeletedObstructionIDsBulk, setEditedObstruction, resetEditedObstructions,
	setDrawnObsExists, setSelectedObstructionId, setObstructionEditPointAction, setInvalidObstructionState, setConfirmedExceedAzimuthWarningFacet,
	setIsTransforming, setPendingAzimuthConfirmationFacet,
	toggleGuideMode, setGuideMode, toggleSnapMode, setSnapMode,
	toggleHideSatelliteOverlay, setActiveDragFunctionality, setMapOnlyFrozenPoint,
} = ToolNewPostionSlice.actions;

export default ToolNewPostionSlice.reducer;
